import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import logoImage from '../assets/logo.png';
import { useAuth } from '../contexts/AuthContext';

const Header: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const { isAuthenticated, logout, currentUser, userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <header className="bg-white shadow-md py-4">
        <div className="container mx-auto px-4">
          {/* Fila elástica: con seis ítems de menú el título centrado en absoluto
              se solapaba con el nombre de usuario. Ahora el título viaja con el
              logo y el menú envuelve en pantallas angostas. */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-4 shrink-0">
              <img 
                src={logoImage} 
                alt="Logo Gappex" 
                className="h-12" 
              />
              <h1 className="text-2xl font-poppins font-semibold text-gappex-primary">
                Validación Suscriptor Gappex
              </h1>
            </div>
            <nav className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2 ml-auto">
              {isAuthenticated && currentUser && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-gappex-primary">{currentUser}</span>
                  {userRole && (
                    <span className="ml-1 text-xs text-gray-500">({userRole})</span>
                  )}
                </div>
              )}
              <ul className="flex flex-wrap justify-end gap-x-6 gap-y-2">
                <li>
                  <Link
                    to="/"
                    className={`transition-colors duration-200 ${
                      location.pathname === '/'
                        ? 'text-gappex-primary font-medium'
                        : 'text-gappex-secondary hover:text-gappex-primary'
                    }`}
                  >
                    Inicio
                  </Link>
                </li>
                <li>
                  <Link
                    to="/planes"
                    className={`transition-colors duration-200 ${
                      location.pathname === '/planes'
                        ? 'text-gappex-primary font-medium'
                        : 'text-gappex-secondary hover:text-gappex-primary'
                    }`}
                  >
                    Planes
                  </Link>
                </li>
                {['admin', 'manager'].includes(userRole ?? '') && (
                  <li>
                    <Link
                      to="/dashboard"
                      className={`transition-colors duration-200 ${
                        location.pathname === '/dashboard'
                          ? 'text-gappex-primary font-medium'
                          : 'text-gappex-secondary hover:text-gappex-primary'
                      }`}
                    >
                      Dashboard
                    </Link>
                  </li>
                )}
                {['admin', 'manager'].includes(userRole ?? '') && (
                  <li>
                    <Link
                      to="/calculadora"
                      className={`transition-colors duration-200 ${
                        location.pathname === '/calculadora'
                          ? 'text-gappex-primary font-medium'
                          : 'text-gappex-secondary hover:text-gappex-primary'
                      }`}
                    >
                      Calculadora
                    </Link>
                  </li>
                )}
                {['admin', 'manager'].includes(userRole ?? '') && (
                  <li>
                    <Link
                      to="/altas"
                      className={`transition-colors duration-200 ${
                        location.pathname === '/altas'
                          ? 'text-gappex-primary font-medium'
                          : 'text-gappex-secondary hover:text-gappex-primary'
                      }`}
                    >
                      Cargar altas
                    </Link>
                  </li>
                )}
                {['admin', 'manager'].includes(userRole ?? '') && (
                  <li>
                    <Link
                      to="/altas/seguimiento"
                      className={`transition-colors duration-200 ${
                        location.pathname === '/altas/seguimiento'
                          ? 'text-gappex-primary font-medium'
                          : 'text-gappex-secondary hover:text-gappex-primary'
                      }`}
                    >
                      Seguimiento de altas
                    </Link>
                  </li>
                )}
                {userRole === 'admin' && (
                  <li>
                    <Link
                      to="/logs"
                      className={`transition-colors duration-200 ${
                        location.pathname === '/logs'
                          ? 'text-gappex-primary font-medium'
                          : 'text-gappex-secondary hover:text-gappex-primary'
                      }`}
                    >
                      Logs
                    </Link>
                  </li>
                )}
                <li>
                  <button
                    onClick={() => setShowModal(true)}
                    className="text-gappex-secondary hover:text-gappex-primary transition-colors duration-200"
                  >
                    Ayuda
                  </button>
                </li>
                {isAuthenticated && (
                  <li>
                    <button
                      onClick={handleLogout}
                      className="text-gappex-secondary hover:text-gappex-primary transition-colors duration-200"
                    >
                      Cerrar Sesión
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          </div>
        </div>
      </header>

      {/* Modal de Ayuda */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-poppins font-semibold text-gappex-primary">Ayuda - Validación de Suscriptores</h2>
                <button 
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="prose max-w-none">
                <h3 className="text-lg font-medium mb-2">¿Cómo buscar un suscriptor?</h3>
                <p className="mb-4">
                  Para buscar un suscriptor, selecciona el tipo de documento e ingresa el número correspondiente.
                </p>
                <ul className="list-disc pl-5 mb-4">
                  <li>Selecciona el tipo de documento (DNI, L.E., Pasaporte, Visa, L.C. o C.I.)</li>
                  <li>Ingresa el número de documento sin puntos ni espacios</li>
                </ul>

                <h3 className="text-lg font-medium mb-2">Información mostrada</h3>
                <p className="mb-4">
                  Los resultados mostrarán:
                </p>
                <ul className="list-disc pl-5 mb-4">
                  <li>Datos personales del suscriptor</li>
                  <li>Estado de la suscripción</li>
                  <li>Información de contacto</li>
                  <li>Planes activos</li>
                </ul>

                <div className="bg-blue-50 p-4 rounded-lg mt-6">
                  <p className="text-sm text-gray-600">
                    ¿Necesitas más ayuda? Contáctanos en:{' '}
                    <a href="mailto:contacto@didesa.com.ar" className="text-gappex-primary hover:text-blue-700">
                      contacto@didesa.com.ar
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header; 